export type ThemePreference = 'light' | 'dark' | 'system';
export type ColorScheme = 'light' | 'dark';

const LIGHT_CLASS = 'day-theme';
const DARK_CLASS = 'dark-theme';
const MANTINE_ATTRIBUTE = 'data-mantine-color-scheme';

let currentPreference: ThemePreference = 'dark';
let currentColorScheme: ColorScheme = 'dark';
let systemMediaQuery: MediaQueryList | null = null;
let systemListener: ((event: MediaQueryListEvent) => void) | null = null;

const listeners = new Set<(scheme: ColorScheme) => void>();

const notifyListeners = (scheme: ColorScheme) => {
  currentColorScheme = scheme;
  for (const listener of listeners) {
    listener(scheme);
  }
};

const supportsDom = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const getRootElement = () => (supportsDom() ? document.documentElement : null);

const cleanupSystemListener = () => {
  if (systemMediaQuery && systemListener) {
    systemMediaQuery.removeEventListener('change', systemListener);
  }
  systemMediaQuery = null;
  systemListener = null;
};

const applyThemeClass = (scheme: ColorScheme) => {
  const root = getRootElement();
  if (!root) {
    return;
  }

  root.classList.toggle(LIGHT_CLASS, scheme === 'light');
  root.classList.toggle(DARK_CLASS, scheme === 'dark');
  root.setAttribute(MANTINE_ATTRIBUTE, scheme);
  root.style.colorScheme = scheme;
  root.dataset.theme = scheme;
};

export const getSystemColorScheme = (): ColorScheme => {
  if (!supportsDom()) {
    return 'dark';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const applyThemePreference = (preference: ThemePreference): ColorScheme => {
  if (!supportsDom()) {
    currentPreference = preference;
    return currentColorScheme;
  }

  currentPreference = preference;
  cleanupSystemListener();

  const resolvedScheme: ColorScheme = preference === 'system' ? getSystemColorScheme() : preference;
  applyThemeClass(resolvedScheme);
  notifyListeners(resolvedScheme);

  if (preference === 'system') {
    systemMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    systemListener = (event: MediaQueryListEvent) => {
      const scheme: ColorScheme = event.matches ? 'dark' : 'light';
      applyThemeClass(scheme);
      notifyListeners(scheme);
    };
    systemMediaQuery.addEventListener('change', systemListener);
  }

  return resolvedScheme;
};

export const getCurrentThemePreference = () => currentPreference;

export const getCurrentColorScheme = () => currentColorScheme;

export const subscribeToColorScheme = (listener: (scheme: ColorScheme) => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
